import { useState, useEffect, useContext } from "react";
import { useParams } from "react-router";
import { useOutletContext } from "react-router-dom";
import GoogleMapReact from "google-map-react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import { LoaderContext } from "../../context/LoaderContext";
import usePatchPatientMutation from "../../hooks/usePatchPatientMutation";

const PatientInfo = () => {
  const { _id_ } = useParams();
  const loaderContext = useContext(LoaderContext);

  const {
    infoData: PatientData,
    setActive,
    setBreadcrumbTitle,
    isTrialLocked,
  } = useOutletContext();
  setActive("info");
  setBreadcrumbTitle("Personal Info");

  const patchPatientMutation = usePatchPatientMutation();

  const UpdateInfo = async (instance) => {
    const updateData = {
      firstName: instance.firstName,
      lastName: instance.lastName,
      phone: instance.phone,
      birth_date: instance.birth_date,
      gender: instance.gender,
      email: instance.email,
      skinTypeId: instance.skin_type,
    };

    loaderContext.setLoaderCount((prev) => prev + 1);

    try {
      await patchPatientMutation.mutateAsync({ patientId: _id_, data: updateData });
    } catch (error) {
      console.error(error);
    } finally {
      loaderContext.setLoaderCount((prev) => prev - 1);
    }
  };

  const AnyReactComponent = () => (
    <div>
      <img
        style={{ height: "35px" }}
        src="https://cdn4.iconfinder.com/data/icons/small-n-flat/24/map-marker-512.png"
      />
    </div>
  );

  const [InfoData, setInfoData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    birth_date: "",
    gender: "",
    email: "",
    skin_type: "",
    participant_id: "",
  });

  useEffect(() => {
    PatientData &&
      setInfoData({
        ...InfoData,
        firstName: PatientData?.first_name,
        lastName: PatientData?.last_name,
        phone: PatientData?.phone,
        birth_date: PatientData?.birth_date,
        gender: PatientData?.gender,
        email: PatientData?.email,
        skin_type: PatientData?.appiell_patient && PatientData?.appiell_patient[0]?.skin_type?.id,
        participant_id: PatientData?.participant_id,
      });
  }, [PatientData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInfoData((InfoData) => ({
      ...InfoData,
      [name]: value,
    }));
  };

  const UpdateInstance = (e) => {
    e.preventDefault();
    UpdateInfo(InfoData);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <form onSubmit={UpdateInstance}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h5">Subject Info</Typography>
          <Button type="submit" variant="contained" size="small" disabled={isTrialLocked}>
            Update
          </Button>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
          }}
        >
          <TextField
            label="First Name"
            name="firstName"
            fullWidth
            size="small"
            value={InfoData.firstName || ""}
            onChange={handleChange}
          />
          <TextField
            label="Last Name"
            name="lastName"
            fullWidth
            size="small"
            value={InfoData.lastName || ""}
            onChange={handleChange}
          />
          <TextField
            label="Email Address"
            name="email"
            type="email"
            fullWidth
            size="small"
            value={InfoData.email || ""}
            onChange={handleChange}
          />
          <TextField
            label="Phone Number"
            name="phone"
            type="tel"
            fullWidth
            size="small"
            value={InfoData.phone || ""}
            onChange={handleChange}
          />
          <FormControl fullWidth size="small">
            <InputLabel id="gender-label">Gender</InputLabel>
            <Select
              labelId="gender-label"
              label="Gender"
              name="gender"
              value={InfoData.gender || ""}
              onChange={handleChange}
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="male">Male</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Birth Date"
            name="birth_date"
            type="date"
            fullWidth
            size="small"
            value={InfoData.birth_date || ""}
            onChange={handleChange}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <FormControl fullWidth size="small">
            <InputLabel id="skin-type-label">Skin Type</InputLabel>
            <Select
              labelId="skin-type-label"
              label="Skin Type"
              name="skin_type"
              value={InfoData?.skin_type || ""}
              onChange={handleChange}
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              <MenuItem value="1">1 - Pale white skin</MenuItem>
              <MenuItem value="2">2 - Fair white skin, light or dark hair</MenuItem>
              <MenuItem value="3">3 - Darker white skin, darker shades hair</MenuItem>
              <MenuItem value="4">4 - Light brown skin, dark hair</MenuItem>
              <MenuItem value="5">5 - Brown skin, dark hair</MenuItem>
              <MenuItem value="6">6 - Dark brown or black skin, dark hair</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Device"
            fullWidth
            size="small"
            value={(PatientData.appiell_patient && PatientData.appiell_patient[0]?.device) || ""}
            disabled
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Experience
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Treatment Type</TableCell>
                  <TableCell>Drug</TableCell>
                  <TableCell>Area</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {PatientData?.experience?.map((data) => (
                  <TableRow key={data.id}>
                    <TableCell>{data.treatment_type}</TableCell>
                    <TableCell>{data.drugs}</TableCell>
                    <TableCell>{data.facial_area}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{ mt: 3 }}>
          <TextField
            label="Location"
            fullWidth
            size="small"
            value={PatientData.location_dict?.Address || ""}
            disabled
          />
          {PatientData.location_dict && (
            <Box
              sx={{
                height: 300,
                width: "100%",
                borderRadius: 1,
                overflow: "hidden",
                mt: 2,
              }}
            >
              <GoogleMapReact
                bootstrapURLKeys={{
                  key: "AIzaSyBykNn93LjLI2MEv2PGOnSfpD_11zi1q30",
                  language: "en",
                }}
                defaultCenter={{
                  lat: parseFloat(
                    PatientData.location_dict ? PatientData.location_dict.Latitude : 37.679878
                  ),
                  lng: parseFloat(
                    PatientData.location_dict ? PatientData.location_dict.Logitude : -95.459778
                  ),
                }}
                defaultZoom={13}
              >
                <AnyReactComponent
                  lat={parseFloat(
                    PatientData.location_dict ? PatientData.location_dict.Latitude : 37.679878
                  )}
                  lng={parseFloat(
                    PatientData.location_dict ? PatientData.location_dict.Logitude : -95.459778
                  )}
                />
              </GoogleMapReact>
            </Box>
          )}
        </Box>
      </form>
    </Paper>
  );
};

export default PatientInfo;
